// theme/presets/cardPresets.ts — curated card material recipes.
//
// Each preset sets colour AND material together so one click produces a finished look.
// The manual colour wheel + material sliders stay fully visible underneath as the
// fine-tune tier. `obsidian` writes strength 0 with every material scalar at its shipped
// default, so it resolves to the stock surface byte-for-byte — the original look is
// always exactly one click away.
//
// All six resolutions were verified against the AA band (see cardSurface.ts): the worst
// case across both modes is textPrimary 13.18:1 / textMuted 4.51:1.

/** The six card keys a preset writes. UserPreferences satisfies this structurally. */
export interface CardRecipeValues {
  cardColor: string;
  cardColorStrength: number;
  cardSheen: number;
  cardGrain: number;
  cardEdge: number;
  cardElevation: number;
}

export interface CardRecipe {
  label: string;
  /** Hue/chroma seed. Empty = leave the user's picked colour alone (strength is 0 anyway). */
  seed: string;
  strength: number;
  sheen: number;
  grain: number;
  edge: number;
  elevation: number;
}

export const CARD_PRESETS = {
  obsidian: { label: 'Obsidian',  seed: '',        strength: 0,    sheen: 0,    grain: 0,    edge: 0,    elevation: 0.5 },
  graphite: { label: 'Graphite',  seed: '#8A8F94', strength: 0.5,  sheen: 0.5,  grain: 0.5,  edge: 0.35, elevation: 0.45 },
  slate:    { label: 'Slate',     seed: '#3E6E8E', strength: 0.55, sheen: 0.45, grain: 0,    edge: 0.5,  elevation: 0.55 },
  midnight: { label: 'Midnight',  seed: '#2A2FA0', strength: 0.6,  sheen: 0.3,  grain: 0.25, edge: 0.55, elevation: 0.7 },
  ink:      { label: 'Ink',       seed: '#6B4FA8', strength: 0.55, sheen: 0.25, grain: 0.5,  edge: 0.45, elevation: 0.45 },
  sand:     { label: 'Warm Sand', seed: '#C89B5A', strength: 0.5,  sheen: 0.2,  grain: 0.75, edge: 0.3,  elevation: 0.35 },
} as const satisfies Record<string, CardRecipe>;

export type CardPresetKey = keyof typeof CARD_PRESETS;

export const CARD_PRESET_KEYS = Object.keys(CARD_PRESETS) as CardPresetKey[];

/** The six pref values a preset click writes (seed '' keeps the user's current colour). */
export function presetValues(key: CardPresetKey, currentColor: string): CardRecipeValues {
  const r = CARD_PRESETS[key];
  return {
    cardColor: r.seed || currentColor,
    cardColorStrength: r.strength,
    cardSheen: r.sheen,
    cardGrain: r.grain,
    cardEdge: r.edge,
    cardElevation: r.elevation,
  };
}

const near = (a: number, b: number): boolean => Math.abs(a - b) < 1e-6;

/**
 * The active preset is DERIVED, never stored — so it can never drift out of sync with
 * the individual sliders. Seed is compared only when strength > 0 (at strength 0 the
 * colour is inert).
 */
export function matchPreset(p: CardRecipeValues): CardPresetKey | null {
  for (const key of CARD_PRESET_KEYS) {
    const r = CARD_PRESETS[key];
    if (
      near(p.cardColorStrength, r.strength) &&
      near(p.cardSheen, r.sheen) &&
      near(p.cardGrain, r.grain) &&
      near(p.cardEdge, r.edge) &&
      near(p.cardElevation, r.elevation) &&
      (r.strength <= 0 || p.cardColor.toLowerCase() === r.seed.toLowerCase())
    ) {
      return key;
    }
  }
  return null;
}
